import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import DataTable from "../../components/common/DataTable";
import { api_paths } from "../../config/api.paths";
import API from "../../config/axios";
import { formatToUSDate } from "../../utils/dateFormat";

// 🔹 Status Badge Component
const StatusBadge = ({ status }) => {
  const classes = {
    Active: "badge bg-success-subtle text-success",
    Free: "badge bg-warning-subtle text-warning",
    Expired: "badge bg-danger-subtle text-danger",
    Pending: "badge bg-danger-subtle text-danger",
  };

  return (
    <span className={`${classes[status]} px-3 py-2 rounded-3`}>{status}</span>
  );
};

export default function ManageMembership() {
  const { user } = useSelector((state) => state.USER_DATA);
  const [members, setMembers] = useState([]);
  const [plan, setPlan] = useState(null);

  // 🔹 Prepare Primary User Row
  const primaryRow = useMemo(() => {
    if (!user) return null;

    const cleanDate = user.date_of_birth?.split("T")[0];
    return {
      _id: "primary-row",
      first_name: user.first_name,
      last_name: user.last_name,
      date_of_birth: cleanDate ? formatToUSDate(cleanDate) : "",
      gender: user.gender
        ? user.gender.charAt(0).toUpperCase() + user.gender.slice(1)
        : "",
      marital_status: user.marital_status || "",
      relationship: "Primary",
      is_membership_active: user.is_membership_active,
      is_child: false,
      membership_id: user.membership_id,
      membership_expiry_date: user.membership_expiry_date,
    };
  }, [user]);

  // 🔹 Fetch API Data
  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [memberRes, planRes] = await Promise.all([
          API.get(api_paths.member.family_member.GET_MEMBER),
          API.get(api_paths.member.membership.GET_PLAN),
        ]);

        setMembers(memberRes.data?.payload?.result || []);

        const plans = planRes.data?.payload?.result || [];
        if (plans.length > 0) setPlan(plans[0]);
      } catch (err) {
        console.error("Error loading data:", err);
      }
    };

    fetchAll();
  }, []);

  // 🔹 Determine membership status
  const getMembershipStatus = (m) => {
    if (m.is_child) return "Free";
    if (m.is_membership_active) return "Active";
    if (m.membership_id) return "Expired";
    return "Pending";
  };

  // 🔹 Combine user + family members
  const allMembers = useMemo(
    () => (primaryRow ? [primaryRow, ...members] : []),
    [primaryRow, members]
  );

  // 🔹 Split Active + Pending
  const { activeMembers, pendingMembers } = useMemo(() => {
    const active = [];
    const pending = [];

    allMembers.forEach((m) => {
      const status = getMembershipStatus(m);
      if (status === "Active") active.push(m);
      else pending.push(m);
    });

    return { activeMembers: active, pendingMembers: pending };
  }, [allMembers]);

  // 🔹 Table Columns
  const baseColumns = [
    {
      key: "name",
      label: "Name",
      render: (_, row) => `${row.first_name} ${row.last_name}`.trim(),
    },
    {
      key: "date_of_birth",
      label: "Date of Birth",
      render: (val) => (val ? formatToUSDate(val) : "-"),
    },
    { key: "gender", label: "Gender" },
    { key: "relationship", label: "Relation" },
    {
      key: "membership_status",
      label: "Membership",
      render: (_, row) => <StatusBadge status={getMembershipStatus(row)} />,
    },
  ];

  const activeColumns = [
    ...baseColumns,
    {
      key: "membership_expiry_date",
      label: "Expiry Date",
      render: (val) => (val ? formatToUSDate(val.split("T")[0]) : "-"),
    },
  ];

  const pendingColumns = [
    ...baseColumns,
    {
      key: "membership_expiry_date",
      label: "Expired At",
      render: (val, row) =>
        getMembershipStatus(row) === "Expired" && val
          ? formatToUSDate(val.split("T")[0])
          : "-",
    },
  ];

  const billablePendingMembers = useMemo(
    () => pendingMembers.filter((m) => getMembershipStatus(m) !== "Free"),
    [pendingMembers]
  );

  // 🔹 Billing Computation
  const memberCount = billablePendingMembers.length;
  const amount = plan?.amount || 0;
  const discount = plan?.discount_amount || 0;

  const totalDiscount = memberCount * discount;
  const totalAmount = memberCount * amount - totalDiscount;

  return (
    <div className="container py-4">
      {/* ACTIVE MEMBERS */}
      <div className="mb-4">
        <div className="d-flex justify-content-between align-items-center">
          <h4 className="inter-bold">Membership</h4>
          {/* <button className="btn btn-orange-lg mb-4">
            Transactions
          </button> */}
        </div>

        <DataTable columns={activeColumns} data={activeMembers} />
      </div>

      {/* PENDING MEMBERS */}
      <h4 className="inter-bold">Pending</h4>
      <DataTable columns={pendingColumns} data={pendingMembers} />

      {/* BILLING BOX */}
      {plan && (
        <div
          className="d-flex justify-content-between align-items-center p-3 rounded-3 mt-3"
          style={{
            backgroundColor: "#FFF7EB",
            border: "1px solid #FFE8C7",
          }}
        >
          {/* LEFT */}
          <div>
            <div className="fw-semibold">Membership Fees</div>
            <div className="text-muted" style={{ fontSize: "14px" }}>
              {plan.plan_name}
            </div>
          </div>

          {/* MIDDLE */}
          <div className="text-center">
            <div className="fw-semibold">
              Total Member(s) {memberCount} × ${amount}
            </div>
            {plan.discount_amount && (
              <div className="text-muted" style={{ fontSize: "14px" }}>
                (${totalDiscount} discount applied)
              </div>
            )}
          </div>

          {/* RIGHT + BUTTON */}
          <div className="d-flex align-items-center gap-3 text-end">
            <div>
              {plan.discount_amount && (
                <div>
                  Total Discount:{" "}
                  <span className="fw-semibold">-${totalDiscount}</span>
                </div>
              )}

              <div>
                Total Amount: <span className="fw-bold">${totalAmount}</span>
              </div>
            </div>

            <button className="btn btn-orange-lg px-4">Process to Pay</button>
          </div>
        </div>
      )}
    </div>
  );
}
